// src/components/files/SearchResults.tsx
import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  alpha,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MovieIcon from '@mui/icons-material/Movie';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { useTranslation } from 'react-i18next';
import { useFileManager } from '../../contexts/FileManagerContext';
import { SearchResultItem } from '../../types/files';
import { formatFileSize } from '../../services/fileService';

// Get icon based on file type
const getFileIcon = (item: SearchResultItem) => {
  if (item.type === 'folder') {
    return <FolderIcon sx={{ color: item.color || '#FFC107', fontSize: 40 }} />;
  }

  const mimeType = item.file_type || '';
  if (mimeType.startsWith('image/')) return <ImageIcon sx={{ color: '#4CAF50', fontSize: 40 }} />;
  if (mimeType === 'application/pdf') return <PictureAsPdfIcon sx={{ color: '#F44336', fontSize: 40 }} />;
  if (mimeType.startsWith('video/')) return <MovieIcon sx={{ color: '#9C27B0', fontSize: 40 }} />;
  if (mimeType.startsWith('audio/')) return <AudiotrackIcon sx={{ color: '#FF9800', fontSize: 40 }} />;
  return <DescriptionIcon sx={{ color: '#2196F3', fontSize: 40 }} />;
};

// Get small icon for list view
const getSmallFileIcon = (item: SearchResultItem) => {
  if (item.type === 'folder') {
    return <FolderIcon sx={{ color: item.color || '#FFC107' }} />;
  }

  const mimeType = item.file_type || '';
  if (mimeType.startsWith('image/')) return <ImageIcon sx={{ color: '#4CAF50' }} />;
  if (mimeType === 'application/pdf') return <PictureAsPdfIcon sx={{ color: '#F44336' }} />;
  if (mimeType.startsWith('video/')) return <MovieIcon sx={{ color: '#9C27B0' }} />;
  if (mimeType.startsWith('audio/')) return <AudiotrackIcon sx={{ color: '#FF9800' }} />;
  return <InsertDriveFileIcon sx={{ color: '#2196F3' }} />;
};

interface SearchResultCardProps {
  item: SearchResultItem;
  onDoubleClick: () => void;
  isSelected: boolean;
  onSelect: (e: React.MouseEvent) => void;
}

const SearchResultCard: React.FC<SearchResultCardProps> = ({
  item,
  onDoubleClick,
  isSelected,
  onSelect,
}) => {
  const { t } = useTranslation();
  const displayName = item.type === 'folder' ? item.name : (item.name || item.filename);
  const location = item.parent_name || t('fileManager.search.rootFolder', 'Mon espace');

  return (
    <Paper
      elevation={isSelected ? 3 : 0}
      onClick={onSelect}
      onDoubleClick={onDoubleClick}
      sx={{
        p: 2,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: isSelected ? 'primary.main' : 'grey.200',
        bgcolor: isSelected ? alpha('#1976d2', 0.08) : 'background.paper',
        transition: 'all 0.15s ease',
        '&:hover': {
          borderColor: 'primary.light',
          bgcolor: isSelected ? alpha('#1976d2', 0.12) : 'grey.50',
        },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        height: '100%',
        minHeight: 140,
      }}
    >
      <Box sx={{ mb: 1.5 }}>
        {getFileIcon(item)}
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
          mb: 0.5,
        }}
      >
        {displayName}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <FolderOpenIcon sx={{ fontSize: 14, color: 'grey.400' }} />
        <Typography
          variant="caption"
          sx={{
            color: 'grey.500',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 120,
          }}
        >
          {location}
        </Typography>
      </Box>
      {item.type === 'document' && item.file_size && (
        <Typography variant="caption" sx={{ color: 'grey.400', mt: 0.5 }}>
          {formatFileSize(item.file_size)}
        </Typography>
      )}
    </Paper>
  );
};

interface SearchResultRowProps {
  item: SearchResultItem;
  onDoubleClick: () => void;
  isSelected: boolean;
  onSelect: (e: React.MouseEvent) => void;
}

const SearchResultRow: React.FC<SearchResultRowProps> = ({
  item,
  onDoubleClick,
  isSelected,
  onSelect,
}) => {
  const { t } = useTranslation();
  const displayName = item.type === 'folder' ? item.name : (item.name || item.filename);
  const location = item.parent_name || t('fileManager.search.rootFolder', 'Mon espace');

  return (
    <ListItem
      onClick={onSelect}
      onDoubleClick={onDoubleClick}
      sx={{
        cursor: 'pointer',
        borderRadius: 1,
        mb: 0.5,
        bgcolor: isSelected ? alpha('#1976d2', 0.08) : 'transparent',
        border: '1px solid',
        borderColor: isSelected ? 'primary.main' : 'transparent',
        '&:hover': {
          bgcolor: isSelected ? alpha('#1976d2', 0.12) : 'grey.50',
        },
      }}
    >
      <ListItemIcon>
        {getSmallFileIcon(item)}
      </ListItemIcon>
      <ListItemText
        primary={displayName}
        secondary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FolderOpenIcon sx={{ fontSize: 12, color: 'grey.400' }} />
              <span>{location}</span>
            </Box>
            {item.type === 'document' && item.file_size && (
              <>
                <span>•</span>
                <span>{formatFileSize(item.file_size)}</span>
              </>
            )}
          </Box>
        }
        primaryTypographyProps={{ fontWeight: 500, noWrap: true }}
        secondaryTypographyProps={{ sx: { color: 'grey.500' } }}
      />
      <ListItemSecondaryAction>
        <Chip
          size="small"
          label={item.type === 'folder' ? t('fileManager.search.folder', 'Dossier') : t('fileManager.search.file', 'Fichier')}
          sx={{
            bgcolor: item.type === 'folder' ? 'warning.50' : 'info.50',
            color: item.type === 'folder' ? 'warning.dark' : 'info.dark',
            fontSize: '0.7rem',
          }}
        />
      </ListItemSecondaryAction>
    </ListItem>
  );
};

const SearchResults: React.FC = () => {
  const { t } = useTranslation();
  const { state, selectItem, isSelected, navigateToSearchResult } = useFileManager();
  const { searchResults, searchQuery, viewMode, searching } = state;

  if (!searchResults && !searching) {
    return null;
  }

  const allResults: SearchResultItem[] = [
    ...(searchResults?.folders || []),
    ...(searchResults?.documents || []),
  ];

  const handleDoubleClick = (item: SearchResultItem) => {
    const parentFolderId = item.type === 'folder' ? item.parent_id : item.folder_id;
    navigateToSearchResult(item.type, item.id, parentFolderId ?? null);
  };

  const handleSelect = (item: SearchResultItem, e: React.MouseEvent) => {
    selectItem({ id: item.id, type: item.type }, e.ctrlKey || e.metaKey, e.shiftKey);
  };

  if (searching) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {t('fileManager.search.searching', 'Recherche en cours...')}
        </Typography>
      </Box>
    );
  }

  if (allResults.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {t('fileManager.search.noResults', 'Aucun resultat')}
        </Typography>
        <Typography color="text.secondary">
          {t('fileManager.search.noResultsFor', "Aucun dossier ou fichier trouve pour '{{query}}'", { query: searchQuery })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Results header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('fileManager.search.resultsCount', '{{count}} resultats pour "{{query}}"', {
            count: allResults.length,
            query: searchQuery,
          })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {searchResults?.folders && searchResults.folders.length > 0 && (
            <Chip
              size="small"
              icon={<FolderIcon sx={{ fontSize: 16 }} />}
              label={t('fileManager.search.foldersCount', '{{count}} dossiers', { count: searchResults.folders.length })}
              sx={{ bgcolor: 'warning.50', color: 'warning.dark' }}
            />
          )}
          {searchResults?.documents && searchResults.documents.length > 0 && (
            <Chip
              size="small"
              icon={<InsertDriveFileIcon sx={{ fontSize: 16 }} />}
              label={t('fileManager.search.filesCount', '{{count}} fichiers', { count: searchResults.documents.length })}
              sx={{ bgcolor: 'info.50', color: 'info.dark' }}
            />
          )}
        </Box>
      </Box>

      {/* Results grid/list */}
      {viewMode === 'grid' ? (
        <Grid container spacing={2}>
          {allResults.map((item) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={`${item.type}-${item.id}`}>
              <SearchResultCard
                item={item}
                onDoubleClick={() => handleDoubleClick(item)}
                isSelected={isSelected(item.id, item.type)}
                onSelect={(e) => handleSelect(item, e)}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <List>
          {allResults.map((item) => (
            <SearchResultRow
              key={`${item.type}-${item.id}`}
              item={item}
              onDoubleClick={() => handleDoubleClick(item)}
              isSelected={isSelected(item.id, item.type)}
              onSelect={(e) => handleSelect(item, e)}
            />
          ))}
        </List>
      )}
    </Box>
  );
};

export default SearchResults;
